import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { grievanceAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';
import '../../styles/Grievance.css';

const STATUS_COLORS = {
  PENDING:      'badge-pending',
  ASSIGNED:     'badge-assigned',
  UNDER_REVIEW: 'badge-under_review',
  RESOLVED:     'badge-resolved',
  REJECTED:     'badge-rejected',
  ESCALATED:    'badge-escalated',
  CLOSED:       'badge-closed',
  REOPENED:     'badge-reopened',
};

const PRIORITY_COLORS = {
  LOW:    'badge-low',
  MEDIUM: 'badge-medium',
  HIGH:   'badge-high',
};

const RESPONSE_BUBBLE_CLASS = {
  OFFICER_NOTE:      'officer',
  DEPARTMENT_ACTION: 'dept',
  INFO_REQUEST:      'officer',
  ESCALATION_NOTE:   'escalation',
  RESOLUTION:        'resolution',
  REOPEN_NOTE:       'officer',
  SUBMITTER_REPLY:   'submitter',
};

const DEPT_ROLE_LABELS = {
  BILLING_MANAGER:       'Billing Manager',
  REPORT_GENERATION:     'Report Generation',
  SAMPLE_COLLECTOR:      'Sample Collector',
  LAB_TECH_MICROBIOLOGY: 'Lab – Microbiology',
  LAB_TECH_PATHOLOGY:    'Lab – Pathology',
  LAB_TECH_BIOCHEMISTRY: 'Lab – Biochemistry',
  recepsonist:           'Receptionist',
  HOSTEL_ADMIN:          'Hostel Admin',
  LIBRARY_STAFF:         'Library Staff',
  TRANSPORT_MANAGER:     'Transport Manager',
  FACULTY:               'Faculty',
  ADMIN:                 'Admin',
  FINANCE:               'Finance',
};

// ── Subrole display labels ────────────────────────────────────────────────────
const SUBROLE_ORDER = [
  'DEAN',
  'PROFESSOR_HOD',
  'HEAD_OF_DEPARTMENT',
  'PROFESSOR',
  'ASSOCIATE_PROFESSOR',
  'ASSISTANT_PROFESSOR',
  '',
];

// ── Modal ─────────────────────────────────────────────────────────────────────
const Modal = ({ open, title, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="grievance-modal-overlay" onClick={onClose}>
      <div className="grievance-modal" onClick={e => e.stopPropagation()}>
        <div className="grievance-modal-header">
          <h3 className="grievance-modal-title">{title}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const GrievanceDetailPage = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [grievance,     setGrievance]     = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [escalateUsers,        setEscalateUsers]        = useState([]);
  const [escalateUsersLoading, setEscalateUsersLoading] = useState(false);

  // ── Modal state ─────────────────────────────────────────────────────────────
  const [modal,         setModal]         = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Assign form ─────────────────────────────────────────────────────────────
  const [assignForm, setAssignForm] = useState({
    department_assigned:   '',
    assigned_to_id:        '',   // used for non-faculty roles
    faculty_department_id: '',   // department ID when FACULTY is chosen
    assigned_faculty_id:   '',   // specific faculty user (optional)
    note:                  '',
  });
  const [departmentUsers, setDeptUsers]    = useState([]);
  const [departments,     setDepartments]  = useState([]);
  const [facultyUsers,    setFacultyUsers] = useState([]);
  const [deptLoading,     setDeptLoading]  = useState(false);
  const [facultyLoading,  setFacultyLoading] = useState(false);

  // Ref keeps the committed faculty-user value regardless of re-render timing
  const facultyUserIdRef = useRef('');

  // ── Other forms ─────────────────────────────────────────────────────────────
  const [statusForm,      setStatusForm]      = useState({ status: '', note: '', resolution_notes: '', rejection_reason: '' });
  const [escalateForm,    setEscalateForm]    = useState({ escalated_to: '', reason: '', instructions: '' });
  const [closeReopenForm, setCloseReopenForm] = useState({ action: '', note: '' });

  // ── Reply state ─────────────────────────────────────────────────────────────
  const [replyMsg,  setReplyMsg]  = useState('');
  const [replyType, setReplyType] = useState('OFFICER_NOTE');
  const [replyFile, setReplyFile] = useState(null);
  const [replying,  setReplying]  = useState(false);

  // ── Role helpers ─────────────────────────────────────────────────────────────
  const isOfficer   = user?.role === 'GRIEVANCE_OFFICER' || user?.role === 'ADMIN';
  const isSubmitter = grievance?.submitted_by?.id === user?.id;

  // ── Load detail ──────────────────────────────────────────────────────────────
  useEffect(() => { loadDetail(); }, [id]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const data = await grievanceAPI.getDetail(id);
      setGrievance(data);
    } catch {
      setError('Could not load grievance details.');
    } finally {
      setLoading(false);
    }
  };

  // ── Assign handlers ──────────────────────────────────────────────────────────

  // Step 1 — role/dept dropdown
  const handleAssignDeptChange = async (e) => {
    const role = e.target.value;
    facultyUserIdRef.current = '';
    setAssignForm({
      department_assigned:   role,
      assigned_to_id:        '',
      faculty_department_id: '',
      assigned_faculty_id:   '',
      note:                  assignForm.note,
    });
    setDeptUsers([]);
    setFacultyUsers([]);

    if (!role) return;

    if (role === 'FACULTY') {
      // Load departments list once
      if (departments.length === 0) {
        setDeptLoading(true);
        try {
          const data = await grievanceAPI.getDepartments();
          setDepartments(Array.isArray(data) ? data : []);
        } catch {
          setDepartments([]);
        } finally {
          setDeptLoading(false);
        }
      }
    } else {
      // Non-faculty: load users for this role
      setDeptLoading(true);
      try {
        const data = await grievanceAPI.getUsersByRole(role);
        setDeptUsers(Array.isArray(data) ? data : []);
      } catch {
        setDeptUsers([]);
      } finally {
        setDeptLoading(false);
      }
    }
  };

  // Step 2 — faculty department dropdown
  const handleFacultyDeptChange = async (e) => {
    const deptId = e.target.value;
    facultyUserIdRef.current = '';
    setAssignForm(prev => ({
      ...prev,
      faculty_department_id: deptId,
      assigned_faculty_id:   '',
    }));
    setFacultyUsers([]);
    if (!deptId) return;

    setFacultyLoading(true);
    try {
      const data = await grievanceAPI.getFacultyByDepartment(deptId);
      setFacultyUsers(Array.isArray(data) ? data : []);
    } catch {
      setFacultyUsers([]);
    } finally {
      setFacultyLoading(false);
    }
  };

  // Step 3 — specific faculty picker
  // Uses a ref to guarantee the value is committed before doAssign reads it
  const handleFacultyUserChange = (e) => {
    const val = e.target.value;
    facultyUserIdRef.current = val;
    setAssignForm(prev => ({ ...prev, assigned_faculty_id: val }));
  };

  // ── doAssign ─────────────────────────────────────────────────────────────────
  const doAssign = async () => {
    setActionLoading(true);
    try {
      const payload = { department_assigned: assignForm.department_assigned };

      if (assignForm.department_assigned === 'FACULTY') {
        // Faculty department scope
        if (assignForm.faculty_department_id)
          payload.faculty_department_id = parseInt(assignForm.faculty_department_id, 10);

        // Specific faculty — read from ref for timing safety
        const rawFacultyId = facultyUserIdRef.current || assignForm.assigned_faculty_id;
        const parsedFacultyId = rawFacultyId ? parseInt(rawFacultyId, 10) : null;
        if (parsedFacultyId && !isNaN(parsedFacultyId))
          payload.assigned_to_id = parsedFacultyId;

      } else {
        // Non-faculty specific user
        const parsedUserId = assignForm.assigned_to_id
          ? parseInt(assignForm.assigned_to_id, 10)
          : null;
        if (parsedUserId && !isNaN(parsedUserId))
          payload.assigned_to_id = parsedUserId;
      }

      if (assignForm.note) payload.note = assignForm.note;

      const updated = await grievanceAPI.assign(id, payload);
      setGrievance(updated);
      facultyUserIdRef.current = '';
      setModal(null);
    } catch (e) {
      notify.error(e.response?.data?.detail || 'Error assigning grievance.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Other action handlers ────────────────────────────────────────────────────
  const doStatusUpdate = async () => {
    setActionLoading(true);
    try {
      const updated = await grievanceAPI.updateStatus(id, statusForm);
      setGrievance(updated);
      setModal(null);
    } catch (e) {
      notify.error(e.response?.data?.detail || 'Error updating status.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── doEscalate — updated to send assigned_to_id ───────────────────────────────
const doEscalate = async () => {
  setActionLoading(true);
  try {
    const payload = {
      escalated_to: escalateForm.escalated_to,
      reason:       escalateForm.reason,
    };
    if (escalateForm.instructions)
      payload.instructions  = escalateForm.instructions;
    if (escalateForm.assigned_to_id)
      payload.assigned_to_id = parseInt(escalateForm.assigned_to_id, 10);

    const updated = await grievanceAPI.escalate(id, payload);
    setGrievance(updated);
    setEscalateUsers([]);
    setModal(null);
  } catch (e) {
    notify.error(e.response?.data?.detail || 'Error escalating.');
  } finally {
    setActionLoading(false);
  }
};

  const doCloseReopen = async () => {
    setActionLoading(true);
    try {
      const updated = await grievanceAPI.closeOrReopen(id, closeReopenForm);
      setGrievance(updated);
      setModal(null);
    } catch (e) {
      notify.error(e.response?.data?.detail || 'Error.');
    } finally {
      setActionLoading(false);
    }
  };

  const doReply = async () => {
    if (!replyMsg.trim()) return;
    setReplying(true);
    try {
      const fd = new FormData();
      fd.append('response_type', isSubmitter ? 'SUBMITTER_REPLY' : replyType);
      fd.append('message', replyMsg);
      if (replyFile) fd.append('attachment', replyFile);
      await grievanceAPI.respond(id, fd);
      setReplyMsg('');
      setReplyFile(null);
      await loadDetail();
    } catch {
      notify.error('Failed to add response.');
    } finally {
      setReplying(false);
    }
  };

  // ── handleEscalateTargetChange ────────────────────────────────────────────────
const handleEscalateTargetChange = async (e) => {
  const role = e.target.value;
  setEscalateForm(prev => ({ ...prev, escalated_to: role, assigned_to_id: '' }));
  setEscalateUsers([]);
  if (!role) return;

  setEscalateUsersLoading(true);
  try {
    const data = await grievanceAPI.getUsersByRole(role);
    setEscalateUsers(Array.isArray(data) ? data : []);
  } catch {
    setEscalateUsers([]);
  } finally {
    setEscalateUsersLoading(false);
  }
};



  // ── Helpers ──────────────────────────────────────────────────────────────────
  const formatDate = (d) => d
    ? new Date(d).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

  const dotClass = (log) => {
    if (log.old_status && log.new_status) return 'status-change';
    if (log.action.toLowerCase().includes('escalat')) return 'escalation';
    return 'response';
  };

  // ── Loading / error screens ──────────────────────────────────────────────────
  if (loading) return (
    <DashboardLayout title="Grievance Detail">
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner-large" />
        <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>Loading…</p>
      </div>
    </DashboardLayout>
  );

  if (error || !grievance) return (
    <DashboardLayout title="Grievance Detail">
      <div className="grievance-empty">
        <p>{error || 'Grievance not found.'}</p>
        <button className="btn-grievance-outline" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    </DashboardLayout>
  );

  const g = grievance;

  // ── Selected faculty lookup (for preview card) ───────────────────────────────
  const selectedFaculty = facultyUsers.find(
    u => String(u.id) === String(assignForm.assigned_faculty_id)
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout title="Grievance Detail">
      <div className="grievance-container">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
            marginBottom: '1rem', padding: 0, fontSize: '0.875rem',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem', alignItems: 'start' }}>

          {/* ── Left column ────────────────────────────────────────────────── */}
          <div>

            {/* Header card */}
            <div className="action-panel" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span className="grievance-id">{g.grievance_id}</span>
                    <span className={`grievance-badge ${STATUS_COLORS[g.status]}`}>
                      {g.status.replace('_', ' ')}
                    </span>
                    <span className={`grievance-badge ${PRIORITY_COLORS[g.priority]}`}>
                      {g.priority} Priority
                    </span>
                  </div>
                  <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>
                    {g.subject}
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                    Submitted by{' '}
                    <strong>{g.submitted_by?.full_name || g.submitted_by?.username}</strong>
                    {' '}&bull;{' '}{formatDate(g.created_at)}
                  </p>
                </div>
              </div>

              <div className="grievance-divider" />

              <div className="info-grid">
                <div className="info-item"><label>Category</label><span>{g.category_display}</span></div>
                <div className="info-item">
                  <label>Department</label>
                  <span>{DEPT_ROLE_LABELS[g.department_assigned] || g.department_assigned || '—'}</span>
                </div>
                <div className="info-item">
                  <label>Assigned To</label>
                  <span>{g.assigned_to?.full_name || '—'}</span>
                </div>
                <div className="info-item"><label>Last Updated</label><span>{formatDate(g.updated_at)}</span></div>
                {g.escalated_to && (
                  <div className="info-item"><label>Escalated To</label><span>{g.escalated_to}</span></div>
                )}
                {g.resolved_at && (
                  <div className="info-item"><label>Resolved At</label><span>{formatDate(g.resolved_at)}</span></div>
                )}
              </div>

              <div className="grievance-divider" />

              <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#374151', fontWeight: 600 }}>
                Description
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {g.description}
              </p>

              {g.attachment && (
                <div style={{ marginTop: '0.75rem' }}>
                  <a href={g.attachment} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.875rem', color: '#6366f1', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    View Attachment
                  </a>
                </div>
              )}

              {g.resolution_notes && (
                <>
                  <div className="grievance-divider" />
                  <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#059669' }}>
                    Resolution Notes
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {g.resolution_notes}
                  </p>
                </>
              )}

              {g.rejection_reason && (
                <>
                  <div className="grievance-divider" />
                  <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#dc2626' }}>
                    Rejection Reason
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {g.rejection_reason}
                  </p>
                </>
              )}
            </div>

            {/* Responses */}
            {g.responses?.length > 0 && (
              <div className="action-panel" style={{ marginBottom: '1rem' }}>
                <h3 className="action-panel-title">
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Responses ({g.responses.length})
                </h3>
                {g.responses.map(r => (
                  <div key={r.id}
                    className={`response-bubble ${RESPONSE_BUBBLE_CLASS[r.response_type] || 'dept'}`}>
                    <div className="response-meta">
                      <span className="response-author">
                        {r.responded_by?.full_name || r.responded_by?.username}
                        <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>
                          ({r.response_type.replace(/_/g, ' ')})
                        </span>
                      </span>
                      <span className="response-time">{formatDate(r.created_at)}</span>
                    </div>
                    <p className="response-message">{r.message}</p>
                    {r.attachment && (
                      <a href={r.attachment} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: '0.25rem', display: 'inline-block' }}>
                        Attachment
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Reply box */}
            {g.status !== 'CLOSED' && g.status !== 'REJECTED' && (
              <div className="action-panel">
                <h3 className="action-panel-title">Add Response</h3>
                {isOfficer && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>
                      Response Type
                    </label>
                    <select
                      value={replyType}
                      onChange={e => setReplyType(e.target.value)}
                      style={{ padding: '0.4rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', width: 220 }}
                    >
                      <option value="OFFICER_NOTE">Officer Note</option>
                      <option value="INFO_REQUEST">Request More Info</option>
                      <option value="ESCALATION_NOTE">Escalation Note</option>
                    </select>
                  </div>
                )}
                <textarea
                  value={replyMsg}
                  onChange={e => setReplyMsg(e.target.value)}
                  rows={3}
                  placeholder="Type your response…"
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                  <input
                    type="file"
                    onChange={e => setReplyFile(e.target.files?.[0])}
                    style={{ fontSize: '0.8rem' }}
                  />
                  <button
                    className="btn-grievance-primary"
                    onClick={doReply}
                    disabled={replying || !replyMsg.trim()}
                  >
                    {replying ? 'Sending…' : 'Send Response'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Right column ───────────────────────────────────────────────── */}
          <div>

            {/* Officer action panel */}
            {isOfficer && (
              <div className="action-panel" style={{ marginBottom: '1rem' }}>
                <h3 className="action-panel-title">
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Officer Actions
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {['PENDING', 'REOPENED', 'ESCALATED'].includes(g.status) && (
                    <button
                      className="btn-grievance-primary"
                      onClick={() => setModal('assign')}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      📋 Assign to Department
                    </button>
                  )}
                  {!['CLOSED', 'REJECTED'].includes(g.status) && (
                    <button
                      onClick={() => {
                        setEscalateForm({ escalated_to: '', reason: '', instructions: '' });
                        setModal('escalate');
                      }}
                      style={{
                        width: '100%', padding: '0.625rem 1rem',
                        background: '#fff7ed', color: '#9a3412',
                        border: '1px solid #fed7aa', borderRadius: 8,
                        cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                      }}
                    >
                      ⬆️ Escalate Grievance
                    </button>
                  )}
                  {g.status === 'RESOLVED' && (
                    <button
                      className="btn-grievance-primary"
                      onClick={() => { setCloseReopenForm({ action: 'CLOSE', note: '' }); setModal('close'); }}
                      style={{ width: '100%', textAlign: 'left', background: '#059669' }}
                    >
                      ✅ Close Grievance
                    </button>
                  )}
                  {['CLOSED', 'RESOLVED', 'REJECTED'].includes(g.status) && (
                    <button
                      onClick={() => { setCloseReopenForm({ action: 'REOPEN', note: '' }); setModal('reopen'); }}
                      style={{
                        width: '100%', padding: '0.625rem 1rem',
                        background: '#fce7f3', color: '#9d174d',
                        border: '1px solid #fbcfe8', borderRadius: 8,
                        cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                      }}
                    >
                      🔄 Reopen Grievance
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── Escalation resolver panel (PRINCIPAL / DEAN / ADMIN) ──────────────── */}
            {['ADMIN', 'PRINCIPAL', 'DEAN'].includes(user?.role) &&
              g.escalated_to === user?.role &&
              (g.assigned_to == null || g.assigned_to?.id === user?.id) &&
              ['ESCALATED', 'UNDER_REVIEW'].includes(g.status) && (
              <div className="action-panel" style={{
                marginBottom: '1rem',
                borderLeft: '3px solid #f97316',
              }}>
                <h3 className="action-panel-title">
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  Escalation — {user?.role} Resolution
                </h3>

                {g.escalation_reason && (
                  <div style={{
                    background: '#fff7ed', border: '1px solid #fed7aa',
                    borderRadius: 8, padding: '0.625rem 0.75rem',
                    marginBottom: '0.75rem', fontSize: '0.8rem', color: '#9a3412',
                  }}>
                    <strong>Escalation Reason:</strong> {g.escalation_reason}
                  </div>
                )}

                <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                  This grievance was escalated to you for resolution.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {g.status === 'ESCALATED' && (
                    <button
                      className="btn-grievance-primary"
                      onClick={() => {
                        setStatusForm({ status: 'UNDER_REVIEW', note: '', resolution_notes: '', rejection_reason: '' });
                        setModal('status');
                      }}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      🔍 Mark Under Review
                    </button>
                  )}
                  {['ESCALATED', 'UNDER_REVIEW'].includes(g.status) && (
                    <>
                      <button
                        className="btn-grievance-primary"
                        onClick={() => {
                          setStatusForm({ status: 'RESOLVED', note: '', resolution_notes: '', rejection_reason: '' });
                          setModal('status');
                        }}
                        style={{ width: '100%', textAlign: 'left', background: '#059669' }}
                      >
                        ✅ Mark as Resolved
                      </button>
                      <button
                        className="btn-grievance-danger"
                        onClick={() => {
                          setStatusForm({ status: 'REJECTED', note: '', resolution_notes: '', rejection_reason: '' });
                          setModal('status');
                        }}
                        style={{ width: '100%', textAlign: 'left' }}
                      >
                        ❌ Reject Grievance
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Department action panel */}
            {!isOfficer && (g.assigned_to?.id === user?.id || g.department_assigned === user?.role) && (
              <div className="action-panel" style={{ marginBottom: '1rem' }}>
                <h3 className="action-panel-title">Department Actions</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {g.status === 'ASSIGNED' && (
                    <button
                      className="btn-grievance-primary"
                      onClick={() => {
                        setStatusForm({ status: 'UNDER_REVIEW', note: '', resolution_notes: '', rejection_reason: '' });
                        setModal('status');
                      }}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      🔍 Mark Under Review
                    </button>
                  )}
                  {['ASSIGNED', 'UNDER_REVIEW'].includes(g.status) && (
                    <>
                      <button
                        className="btn-grievance-primary"
                        onClick={() => {
                          setStatusForm({ status: 'RESOLVED', note: '', resolution_notes: '', rejection_reason: '' });
                          setModal('status');
                        }}
                        style={{ width: '100%', textAlign: 'left', background: '#059669' }}
                      >
                        ✅ Mark as Resolved
                      </button>
                      <button
                        className="btn-grievance-danger"
                        onClick={() => {
                          setStatusForm({ status: 'REJECTED', note: '', resolution_notes: '', rejection_reason: '' });
                          setModal('status');
                        }}
                        style={{ width: '100%', textAlign: 'left' }}
                      >
                        ❌ Reject Grievance
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Activity Timeline */}
            <div className="action-panel">
              <h3 className="action-panel-title">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Activity Timeline
              </h3>
              <div className="timeline">
                {g.activity_logs?.map(log => (
                  <div key={log.id} className="timeline-item">
                    <div className={`timeline-dot ${dotClass(log)}`} />
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-actor">
                          {log.performed_by?.full_name || log.performed_by?.username}
                        </span>
                        <span className="timeline-time">{formatDate(log.created_at)}</span>
                      </div>
                      <p className="timeline-text">
                        <strong>{log.action}</strong>
                        {log.old_status && log.new_status && (
                          <span style={{ color: '#6b7280' }}>
                            {' '}· {log.old_status} → {log.new_status}
                          </span>
                        )}
                      </p>
                      {log.details && (
                        <p className="timeline-text" style={{ marginTop: 2, color: '#6b7280' }}>
                          {log.details}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Escalation history */}
            {g.escalation_history?.length > 0 && (
              <div className="action-panel" style={{ marginTop: '1rem' }}>
                <h3 className="action-panel-title">⬆️ Escalation History</h3>
                {g.escalation_history.map(e => (
                  <div key={e.id} style={{
                    marginBottom: '0.75rem', padding: '0.75rem',
                    background: '#fff7ed', borderRadius: 8, borderLeft: '3px solid #f59e0b',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <strong style={{ fontSize: '0.875rem' }}>To: {e.escalated_to}</strong>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{formatDate(e.created_at)}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151' }}>{e.reason}</p>
                    {e.instructions && (
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                        Instructions: {e.instructions}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════
          MODALS
      ════════════════════════════════════════════════════════════ */}

      {/* ── Assign modal ──────────────────────────────────────────────────────── */}
      <Modal
        open={modal === 'assign'}
        title="Assign to Department"
        onClose={() => setModal(null)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Step 1 — Role / Department */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
              Department / Role <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={assignForm.department_assigned}
              onChange={handleAssignDeptChange}
              style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}
            >
              <option value="">Select…</option>
              {Object.entries(DEPT_ROLE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* ── FACULTY path ── */}
          {assignForm.department_assigned === 'FACULTY' && (
            <>
              {/* Step 2 — Faculty Department */}
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Faculty Department
                </label>

                {deptLoading ? (
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', padding: '0.5rem 0' }}>
                    Loading departments…
                  </div>
                ) : (
                  <select
                    value={assignForm.faculty_department_id}
                    onChange={handleFacultyDeptChange}
                    style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}
                  >
                    <option value="">All departments (no filter)</option>
                    {departments.map(d => (
                      <option key={d.id} value={String(d.id)}>{d.name}</option>
                    ))}
                  </select>
                )}

                <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  {assignForm.faculty_department_id
                    ? 'Only faculty in this department will see the grievance.'
                    : 'All faculty across every department will see the grievance.'}
                </p>
              </div>

              {/* Step 3 — Specific faculty (only after dept is chosen) */}
              {assignForm.faculty_department_id && (
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    Assign to specific faculty{' '}
                    <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
                  </label>

                  {facultyLoading ? (
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
                      Loading faculty…
                    </p>
                  ) : facultyUsers.length === 0 ? (
                    <p style={{ fontSize: '0.8rem', color: '#f59e0b', margin: 0 }}>
                      No approved faculty found in this department.
                    </p>
                  ) : (
                    <>
                      <select
                        value={assignForm.assigned_faculty_id}
                        onChange={handleFacultyUserChange}
                        style={{
                          width: '100%', padding: '0.625rem',
                          border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem',
                        }}
                      >
                        <option value="">All faculty in this department</option>
                        {SUBROLE_ORDER.map(subrole => {
                          const group = facultyUsers.filter(
                            u => (u.faculty_subrole || '') === subrole
                          );
                          if (group.length === 0) return null;
                          const groupLabel = subrole
                            ? (group[0].subrole_display || subrole)
                            : 'Faculty';
                          return (
                            <optgroup key={subrole || 'other'} label={`── ${groupLabel} ──`}>
                              {group.map(u => (
                                <option key={u.id} value={String(u.id)}>
                                  {u.full_name ||
                                    `${u.first_name} ${u.last_name}`.trim() ||
                                    u.username}
                                  {u.subrole_display ? ` (${u.subrole_display})` : ''}
                                </option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>

                      {/* Selected faculty preview card */}
                      {selectedFaculty && (
                        <div style={{
                          marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                          background: '#eef2ff', borderRadius: 8,
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                        }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: '#6366f1', color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                          }}>
                            {(
                              selectedFaculty.first_name?.[0] ||
                              selectedFaculty.username?.[0] ||
                              '?'
                            ).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3730a3' }}>
                              {selectedFaculty.full_name ||
                                `${selectedFaculty.first_name} ${selectedFaculty.last_name}`.trim()}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#6366f1' }}>
                              {selectedFaculty.subrole_display || 'Faculty'}
                              {selectedFaculty.department_name
                                ? ` · ${selectedFaculty.department_name}`
                                : ''}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              facultyUserIdRef.current = '';
                              setAssignForm(p => ({ ...p, assigned_faculty_id: '' }));
                            }}
                            style={{
                              marginLeft: 'auto', background: 'none', border: 'none',
                              cursor: 'pointer', color: '#6366f1', fontSize: '0.75rem',
                              padding: '0.125rem 0.25rem',
                            }}
                          >
                            ✕ Clear
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Non-faculty path ── */}
          {assignForm.department_assigned && assignForm.department_assigned !== 'FACULTY' && (
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Assign to specific user{' '}
                <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
              </label>

              {deptLoading ? (
                <div style={{ fontSize: '0.8rem', color: '#6b7280', padding: '0.5rem 0' }}>
                  Loading users…
                </div>
              ) : departmentUsers.length === 0 ? (
                <div style={{
                  fontSize: '0.8rem', color: '#6b7280',
                  background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: 6, padding: '0.5rem 0.75rem',
                }}>
                  No specific users found — grievance will be visible to anyone with this role.
                </div>
              ) : (
                <select
                  value={assignForm.assigned_to_id}
                  onChange={e => setAssignForm(p => ({ ...p, assigned_to_id: e.target.value }))}
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}
                >
                  <option value="">Any user in this department</option>
                  {departmentUsers.map(u => (
                    <option key={u.id} value={String(u.id)}>
                      {u.first_name} {u.last_name}
                      {u.username ? ` (${u.username})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Internal note */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
              Internal Note{' '}
              <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
            </label>
            <textarea
              value={assignForm.note}
              onChange={e => setAssignForm(p => ({ ...p, note: e.target.value }))}
              rows={3}
              placeholder="Add a note for the department…"
              style={{
                width: '100%', padding: '0.625rem', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Confirm / Cancel */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn-grievance-primary"
              style={{ flex: 1 }}
              disabled={
                actionLoading ||
                !assignForm.department_assigned ||
                deptLoading ||
                facultyLoading
              }
              onClick={doAssign}
            >
              {actionLoading ? 'Assigning…' : 'Assign'}
            </button>
            <button className="btn-grievance-outline" onClick={() => setModal(null)}>
              Cancel
            </button>
          </div>

        </div>
      </Modal>

      {/* ── Status update modal ───────────────────────────────────────────────── */}
      <Modal
        open={modal === 'status'}
        title={`Mark as ${statusForm.status?.replace('_', ' ')}`}
        onClose={() => setModal(null)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {statusForm.status === 'RESOLVED' && (
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Resolution Notes
              </label>
              <textarea
                value={statusForm.resolution_notes}
                onChange={e => setStatusForm(p => ({ ...p, resolution_notes: e.target.value }))}
                rows={4}
                placeholder="Describe how the issue was resolved…"
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          )}
          {statusForm.status === 'REJECTED' && (
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Rejection Reason
              </label>
              <textarea
                value={statusForm.rejection_reason}
                onChange={e => setStatusForm(p => ({ ...p, rejection_reason: e.target.value }))}
                rows={3}
                placeholder="Reason for rejection…"
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          )}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
              Additional Note (optional)
            </label>
            <textarea
              value={statusForm.note}
              onChange={e => setStatusForm(p => ({ ...p, note: e.target.value }))}
              rows={2}
              placeholder="Any additional remarks…"
              style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className={statusForm.status === 'REJECTED' ? 'btn-grievance-danger' : 'btn-grievance-primary'}
              style={{ flex: 1 }}
              disabled={actionLoading}
              onClick={doStatusUpdate}
            >
              {actionLoading ? 'Saving…' : 'Confirm'}
            </button>
            <button className="btn-grievance-outline" onClick={() => setModal(null)}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* ── Escalate modal ────────────────────────────────────────────────────── */}
        <Modal
          open={modal === 'escalate'}
          title="Escalate Grievance"
          onClose={() => { setEscalateUsers([]); setModal(null); }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Step 1 — Target role */}
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Escalate To <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={escalateForm.escalated_to}
                onChange={handleEscalateTargetChange}
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}
              >
                <option value="">Select authority…</option>
                <option value="ADMIN">Admin</option>
                <option value="PRINCIPAL">Principal</option>
                <option value="DEAN">Dean</option>
              </select>
            </div>

            {/* Step 2 — Specific user within chosen role */}
            {escalateForm.escalated_to && (
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Assign to specific {escalateForm.escalated_to}{' '}
                  <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
                </label>

                {escalateUsersLoading ? (
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
                    Loading users…
                  </p>
                ) : escalateUsers.length === 0 ? (
                  <div style={{
                    fontSize: '0.8rem', color: '#6b7280',
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: 6, padding: '0.5rem 0.75rem',
                  }}>
                    No users found — grievance will be visible to all {escalateForm.escalated_to} users.
                  </div>
                ) : (
                  <>
                    <select
                      value={escalateForm.assigned_to_id}
                      onChange={e => setEscalateForm(p => ({ ...p, assigned_to_id: e.target.value }))}
                      style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}
                    >
                      <option value="">Any {escalateForm.escalated_to} (all will see it)</option>
                      {escalateUsers.map(u => (
                        <option key={u.id} value={String(u.id)}>
                          {u.first_name} {u.last_name}
                          {u.username ? ` (${u.username})` : ''}
                        </option>
                      ))}
                    </select>

                    {/* Selected user preview */}
                    {escalateForm.assigned_to_id && (() => {
                      const sel = escalateUsers.find(
                        u => String(u.id) === String(escalateForm.assigned_to_id)
                      );
                      if (!sel) return null;
                      return (
                        <div style={{
                          marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                          background: '#fff7ed', borderRadius: 8,
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                        }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: '#f97316', color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                          }}>
                            {(sel.first_name?.[0] || sel.username?.[0] || '?').toUpperCase()}
                          </div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#9a3412' }}>
                            {sel.first_name} {sel.last_name}
                            {sel.username ? ` (${sel.username})` : ''}
                          </div>
                          <button
                            type="button"
                            onClick={() => setEscalateForm(p => ({ ...p, assigned_to_id: '' }))}
                            style={{
                              marginLeft: 'auto', background: 'none', border: 'none',
                              cursor: 'pointer', color: '#f97316', fontSize: '0.75rem',
                            }}
                          >
                            ✕ Clear
                          </button>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* Reason */}
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Reason for Escalation <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={escalateForm.reason}
                onChange={e => setEscalateForm(p => ({ ...p, reason: e.target.value }))}
                rows={3}
                placeholder="Why is this being escalated?"
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            {/* Instructions */}
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Instructions{' '}
                <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
              </label>
              <textarea
                value={escalateForm.instructions}
                onChange={e => setEscalateForm(p => ({ ...p, instructions: e.target.value }))}
                rows={2}
                placeholder="Any instructions for the authority…"
                style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                style={{
                  flex: 1, background: '#f59e0b', color: '#fff', border: 'none',
                  padding: '0.625rem', borderRadius: 8, cursor: 'pointer', fontWeight: 500,
                }}
                disabled={actionLoading || !escalateForm.escalated_to || !escalateForm.reason}
                onClick={doEscalate}
              >
                {actionLoading ? 'Escalating…' : 'Escalate'}
              </button>
              <button
                className="btn-grievance-outline"
                onClick={() => { setEscalateUsers([]); setModal(null); }}
              >
                Cancel
              </button>
            </div>

          </div>
        </Modal>

      {/* ── Close modal ───────────────────────────────────────────────────────── */}
      <Modal
        open={modal === 'close'}
        title="Close Grievance"
        onClose={() => setModal(null)}
      >
        <p style={{ color: '#374151', fontSize: '0.875rem', marginTop: 0 }}>
          Closing this grievance confirms the issue has been resolved to satisfaction.
        </p>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
            Closing Note (optional)
          </label>
          <textarea
            value={closeReopenForm.note}
            onChange={e => setCloseReopenForm(p => ({ ...p, note: e.target.value }))}
            rows={3}
            placeholder="Add a closing remark…"
            style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn-grievance-primary"
            style={{ flex: 1, background: '#059669' }}
            disabled={actionLoading}
            onClick={doCloseReopen}
          >
            {actionLoading ? 'Closing…' : 'Close Grievance'}
          </button>
          <button className="btn-grievance-outline" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </Modal>

      {/* ── Reopen modal ──────────────────────────────────────────────────────── */}
      <Modal
        open={modal === 'reopen'}
        title="Reopen Grievance"
        onClose={() => setModal(null)}
      >
        <p style={{ color: '#374151', fontSize: '0.875rem', marginTop: 0 }}>
          Reopening this grievance will set status to REOPENED for re-investigation.
        </p>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 4 }}>
            Reason for Reopening
          </label>
          <textarea
            value={closeReopenForm.note}
            onChange={e => setCloseReopenForm(p => ({ ...p, note: e.target.value }))}
            rows={3}
            placeholder="Why is this being reopened?"
            style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            style={{
              flex: 1, background: '#9d174d', color: '#fff', border: 'none',
              padding: '0.625rem', borderRadius: 8, cursor: 'pointer', fontWeight: 500,
            }}
            disabled={actionLoading}
            onClick={doCloseReopen}
          >
            {actionLoading ? 'Reopening…' : 'Reopen'}
          </button>
          <button className="btn-grievance-outline" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </Modal>

    </DashboardLayout>
  );
};

export default GrievanceDetailPage;